import React, { useState, useEffect } from 'react'
import DashboardHeader from "../../layouts/dashboard_header";
import SideMenu from "../../layouts/dashboard_menu";
import DashboardFooter from "../../layouts/dashboard_footer";
import BillingStats from "./BillingStats";
import BillingTransactions from "./BillingTransactions";
import BillingWallet from "./BillingWallet";
import BillingGates from "./BillingGates";

interface BillingState {
    activeTab: string;
    userEmail: string;
    userPassword: string;
    isLoggedIn: boolean;
    accountBalance: number;
    userDetails: any;
    transactions: any[];
    gates: any[];
    pockets: any[];
    loading: boolean;
    error: string | null;
}

const BillingHome: React.FC = () => {
    const [state, setState] = useState<BillingState>({
        activeTab: 'overview',
        userEmail: '',
        userPassword: '',
        isLoggedIn: false,
        accountBalance: 0,
        userDetails: null,
        transactions: [],
        gates: [],
        pockets: [],
        loading: false,
        error: null
    });

    const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://64.227.174.0:8004'}/billing/api`;

    const handleLogin = async () => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const response = await fetch(`${API_BASE_URL}/login/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: state.userEmail,
                    user_password: state.userPassword
                })
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                setState(prev => ({ 
                    ...prev, 
                    isLoggedIn: true, 
                    loading: false,
                    userDetails: data.data
                }));
                await fetchAccountBalance();
                await fetchTransactions();
                await fetchGates();
                await fetchPockets();
            } else {
                setState(prev => ({ 
                    ...prev, 
                    loading: false, 
                    error: data.message 
                }));
            }
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                loading: false, 
                error: 'Failed to connect to billing system' 
            }));
        }
    };

    const handleLogout = async () => {
        try {
            await fetch(`${API_BASE_URL}/logout/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: state.userEmail
                })
            });
        } catch (error) {
            console.error('Logout error:', error);
        }
        
        setState(prev => ({
            ...prev,
            isLoggedIn: false,
            accountBalance: 0,
            userDetails: null,
            transactions: [],
            gates: [],
            pockets: []
        }));
    };

    const fetchAccountBalance = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/account-balance/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: state.userEmail
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setState(prev => ({ 
                    ...prev, 
                    accountBalance: data.data.account_balance 
                }));
            }
        } catch (error) {
            console.error('Failed to fetch account balance:', error);
        }
    };

    const fetchTransactions = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/transactions/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: state.userEmail
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setState(prev => ({ 
                    ...prev, 
                    transactions: data.data.transactions || [] 
                }));
            }
        } catch (error) {
            console.error('Failed to fetch transactions:', error);
        }
    };

    const fetchGates = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/gates-list/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: state.userEmail
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setState(prev => ({ 
                    ...prev, 
                    gates: data.data.gates || [] 
                }));
            }
        } catch (error) {
            console.error('Failed to fetch gates:', error);
        }
    };

    const fetchPockets = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/pockets-list/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: state.userEmail
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setState(prev => ({ 
                    ...prev, 
                    pockets: data.data.pockets || [] 
                }));
            }
        } catch (error) {
            console.error('Failed to fetch pockets:', error);
        }
    };

    const renderLoginForm = () => (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
                    <div className="text-center mb-8">
                        <div className="mx-auto h-16 w-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mb-4">
                            <i className="ri-bank-card-line text-white text-2xl"></i>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">IndexPay Integration</h2>
                        <p className="text-gray-600">Connect to external billing system</p>
                    </div>
                    
                    {state.error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                            <div className="flex items-center">
                                <i className="ri-error-warning-line text-red-500 mr-2"></i>
                                <p className="text-red-600 text-sm font-medium">{state.error}</p>
                            </div>
                        </div>
                    )}
                    
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                                Email Address
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <i className="ri-mail-line text-gray-400"></i>
                                </div>
                                <input
                                    type="email"
                                    value={state.userEmail}
                                    onChange={(e) => setState(prev => ({ ...prev, userEmail: e.target.value }))}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    placeholder="Enter your email"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                                Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <i className="ri-lock-line text-gray-400"></i>
                                </div>
                                <input
                                    type="password"
                                    value={state.userPassword}
                                    onChange={(e) => setState(prev => ({ ...prev, userPassword: e.target.value }))}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    placeholder="Enter your password"
                                />
                            </div>
                        </div>
                        
                        <button
                            onClick={handleLogin}
                            disabled={state.loading}
                            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-xl hover:from-blue-700 hover:to-purple-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl"
                        >
                            {state.loading ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Connecting...
                                </div>
                            ) : (
                                <div className="flex items-center justify-center">
                                    <i className="ri-plug-line mr-2"></i>
                                    Connect to IndexPay
                                </div>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderBillingDashboard = () => (
        <div className="space-y-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-xl p-8 text-white">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center">
                    <div className="mb-4 lg:mb-0">
                        <h1 className="text-3xl font-bold mb-2">Uzima Billing Center</h1>
                        <p className="text-blue-100 text-lg">External IndexPay Integration</p>
                        <div className="flex items-center mt-3">
                            <div className="w-3 h-3 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                            <span className="text-sm text-blue-100">Connected to IndexPay</span>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                        <div className="text-right">
                            <p className="text-sm text-blue-200">Logged in as</p>
                            <p className="font-semibold text-lg">{state.userDetails?.name || state.userEmail}</p>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="bg-white bg-opacity-20 backdrop-blur-sm text-white px-6 py-2 rounded-xl hover:bg-opacity-30 transition-all duration-200 border border-white border-opacity-30"
                        >
                            <i className="ri-logout-box-r-line mr-2"></i>
                            Logout
                        </button>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="border-b border-gray-200">
                    <nav className="flex flex-wrap space-x-1 px-6">
                        {[
                            { id: 'overview', label: 'Overview', icon: '📊', color: 'blue' },
                            { id: 'transactions', label: 'Transactions', icon: '💳', color: 'green' },
                            { id: 'wallet', label: 'Wallet', icon: '💰', color: 'purple' },
                            { id: 'gates', label: 'Gates', icon: '🚪', color: 'orange' }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setState(prev => ({ ...prev, activeTab: tab.id }))}
                                className={`py-4 px-6 border-b-2 font-medium text-sm transition-all duration-200 rounded-t-lg ${
                                    state.activeTab === tab.id
                                        ? `border-${tab.color}-500 text-${tab.color}-600 bg-${tab.color}-50`
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                <span className="mr-2 text-lg">{tab.icon}</span>
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="p-8">
                    {state.activeTab === 'overview' && (
                        <BillingStats 
                            accountBalance={state.accountBalance}
                            transactions={state.transactions}
                            gates={state.gates}
                        />
                    )}
                    {state.activeTab === 'transactions' && (
                        <BillingTransactions 
                            userEmail={state.userEmail}
                        />
                    )}
                    {state.activeTab === 'wallet' && (
                        <BillingWallet 
                            accountBalance={state.accountBalance}
                            userEmail={state.userEmail}
                            pockets={state.pockets}
                        />
                    )}
                    {state.activeTab === 'gates' && (
                        <BillingGates 
                            gates={state.gates}
                            userEmail={state.userEmail}
                        />
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="row mt-4">
                            <div className="col-12">
                                {state.isLoggedIn ? renderBillingDashboard() : renderLoginForm()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <DashboardFooter />
        </>
    )
}

export default BillingHome 